import { BrandingProvider } from "@/src/branding/branding-provider.tsx";
import { useBranding } from "@/src/branding/use-branding.hook.ts";
import { LoadLayout } from "@core/presentation/components/feedback/load-layout/load-layout.component.tsx";
import { IotSkeleton } from "@core/presentation/components/feedback/skeleton/iot-skeleton.component.tsx";
import { IotSuspense } from "@core/presentation/components/feedback/suspense/iot-suspense.component.tsx";
import { ToastManager } from "@core/presentation/components/feedback/toast-manager/toast-manager.component.tsx";
import { container } from "@di/inversify.config.ts";
import { useDidMount, useViewModel } from "@iot-platform/core";
import { LoginRoutes } from "@routes/public/login.routes.tsx";
import { App as AntdApp, ConfigProvider } from "antd";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";
import { observer } from "mobx-react";
import type { FC } from "react";
import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router";
import { AppViewModel } from "./app.viewmodel.ts";
import { designTokens } from "./styles/design-tokens.ts";

const primaryColor = "var(--primary-color)";
const secondaryColor = "var(--secondary-color)";

const _App: FC = observer(() => {
    const navigate = useNavigate();
    const PIXELS = 0.01;

    const viewModel = useViewModel(() => container.get(AppViewModel));

    useBranding();

    useDidMount(() => {
        viewModel.attachExit(() => {
            void navigate(LoginRoutes.LOGIN, { replace: true });
        });
    });

    useEffect(() => {
        const setVH = (): void => {
            document.documentElement.style.setProperty(
                "--vh",
                `${window.innerHeight * PIXELS}px`,
            );
        };
        setVH();
        window.addEventListener("resize", setVH);
        return (): void => {
            window.removeEventListener("resize", setVH);
        };
    }, []);

    return (
        <BrandingProvider>
            <IotSkeleton loading={viewModel.loading}>
                <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: primaryColor,
                            colorSuccess: designTokens.color.success,
                            colorWarning: designTokens.color.warning,
                            colorError: designTokens.color.danger,
                            colorInfo: primaryColor,
                            controlHeight: 45,
                            fontFamily: designTokens.font.sans,
                            fontSizeHeading1: 32,
                            fontSizeHeading2: 24,
                            fontSizeHeading3: 24,
                            fontSizeHeading4: 14,
                            fontSizeHeading5: 20,
                        },
                        components: {
                            Input: {
                                colorText: primaryColor,
                                activeBorderColor: primaryColor,
                                hoverBorderColor: primaryColor,
                                activeShadow: "none",
                            },
                            InputNumber: {
                                colorText: secondaryColor,
                                activeBorderColor: primaryColor,
                                hoverBorderColor: primaryColor,
                                activeShadow: "none",
                            },
                            Form: {
                                labelColor: primaryColor,
                            },
                            Menu: {
                                colorText: designTokens.color.white,
                                popupBg: designTokens.color.darkBlack,
                            },
                            Layout: {
                                triggerBg: primaryColor,
                            },
                            Collapse: {
                                contentPadding: 0,
                            },
                            Table: {
                                headerBg: designTokens.color.white,
                                cellPaddingBlock: 4,
                                cellFontSize: 12,
                            },
                            Select: {
                                activeBorderColor: primaryColor,
                                optionActiveBg: designTokens.color.lightGray,
                                optionSelectedBg: designTokens.color.gray,
                                hoverBorderColor: primaryColor,
                                activeOutlineColor: "none",
                                optionSelectedColor: secondaryColor,
                            },
                            DatePicker: {
                                activeBorderColor: primaryColor,
                                hoverBorderColor: primaryColor,
                                cellActiveWithRangeBg:
                                    designTokens.color.lightGray,
                                cellHoverBg: designTokens.color.lightGray,
                                controlItemBgActive:
                                    designTokens.color.lightGray,
                                activeShadow: "none",
                            },
                            Button: {
                                colorPrimaryHover: primaryColor,
                            },
                            Tabs: {
                                inkBarColor: secondaryColor,
                            },
                            Modal: {
                                titleColor: primaryColor,
                                titleFontSize: 24,
                            },
                            Checkbox: {
                                colorPrimary: secondaryColor,
                                colorPrimaryHover: designTokens.color.gray,
                                colorBorder: designTokens.color.gray,
                                motionDurationFast: "0.1s",
                            },
                            Radio: {
                                colorPrimary: secondaryColor,
                                colorPrimaryHover: designTokens.color.gray,
                                colorBorder: designTokens.color.gray,
                                motionDurationFast: "0.1s",
                            },
                            Timeline: {
                                tailColor: primaryColor,
                                dotBg: primaryColor,
                            },
                            Typography: {
                                colorText: primaryColor,
                                colorTextHeading: primaryColor,
                                titleMarginTop: 0,
                                titleMarginBottom: 0,
                            },
                        },
                    }}
                >
                    <AntdApp>
                        <ToastManager />
                        <IotSkeleton loading={viewModel.loading}>
                            <div>
                                <LoadLayout>
                                    <Outlet />
                                </LoadLayout>
                            </div>
                        </IotSkeleton>
                    </AntdApp>
                </ConfigProvider>
            </IotSkeleton>
        </BrandingProvider>
    );
});

_App.displayName = "_App";

export const App: FC = () => (
    <IotSuspense>
        <_App />
    </IotSuspense>
);
